import Follow from '@/components/bookmarks/Follow';
import { Button } from '@/components/ui/Button';
import { Link } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';
import { OrganisationPlaceHolderLogo } from '../utils/Logo';
import { buildOrgSeachJobsUri } from '../utils/buildOrgSeachJobsUri';
import OrganisationNav from './OrganisationNav';

interface groupProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  alias: string;
  button: any;
  content?: any;
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  path: string;
  image: IGatsbyImageData;
  orgName: string;
  orgUrl: string;
  pageCounter: any;
  nav: boolean;
  showSearchJobsBtn?: boolean;
  relatedOrgJobsSearchPageUri?: string | null;
}

/**
 * Organisation Header component
 * @return {JSX}
 */
export default function OrganisationHeadline({
  path,
  orgName,
  orgUrl,
  image,
  pageCounter,
  showSearchJobsBtn = true,
  nav,
  id,
  relatedOrgJobsSearchPageUri
}: Props) {
  // Logo logic
  const orgJobsSearchPageUri = buildOrgSeachJobsUri(orgName);
  const logoMarkUp = (
    <>
      {image ? (
        <div className="p-2">
          <GatsbyImage image={image} alt={orgName + ' Logo'} />
        </div>
      ) : (
        <div className="flex flex-col h-full w-full rounded overflow-hidden">
          <OrganisationPlaceHolderLogo organisationName={orgName} />
        </div>
      )}
    </>
  );

  // Add 'about' to the start of the pageGroupCounts object
  pageCounter.pageGroupCounts = { about: true, ...pageCounter.pageGroupCounts };

  const allTabs: string[] = Object.keys(pageCounter.pageGroupCounts).filter(
    (item) => pageCounter.pageGroupCounts[item] > 0 || item === 'about'
  );

  // Just for this component we only want links not buttons for each tab
  const tabs: groupProps[] = allTabs.map((item) => ({
    label: item === 'gradsims' ? 'GradSims' : item,
    alias: item.toLowerCase(),
    button: null,
    content: null
  }));

  return (
    <div className="mt-3 mb-5 section-container-2xl">
      <div className="section-container-full section-container-indent-t section-container-indent-r">
        <div
          className={`justify-between flex flex-col ${
            orgUrl && showSearchJobsBtn ? 'md:flex-row md:justify-between' : 'md:flex-row md:justify-start'
          } md:gap-y-1 md:gap-x-4`}
        >
          <div className="flex flex-col md:flex-row gap-10">
            <div className={`shadow-sm rounded hidden md:h-28 md:w-28 md:flex items-center bg-white justify-center`}>
              {logoMarkUp}
            </div>
            <div className="flex flex-col gap-5 justify-center">
              <div className="flex gap-4 items-center">
                <div
                  className={`flex-shrink-0 shadow-sm rounded h-20 w-20 flex md:hidden items-center bg-white justify-center mr-5`}
                >
                  {logoMarkUp}
                </div>
                <h1 className={`flex items-center`}>
                  <span className="display-title-2xl display-title-semibold md:display-title-3xl">
                    {orgName}
                    <span className="sr-only"> | </span>
                  </span>
                </h1>
                <div className={`border-l-secondary-1100 flex-shrink-0`}>
                  <Follow id={id} bookmarkBtnId={`orgHeadline_${id}`} size="xs" />
                </div>
              </div>
              <div className="flex gap-2">
                {showSearchJobsBtn && (
                  <Button asChild appearance="primary" size="sm">
                    <Link
                      to={relatedOrgJobsSearchPageUri ?? orgJobsSearchPageUri}
                      data-track={`organisation_search_jobs_visit_click`}
                      data-track-abacus={`organisation_search_jobs_visit_click`}
                      data-track-text="Search jobs & opportunities"
                      data-track-id={id}
                      data-track-target={relatedOrgJobsSearchPageUri ?? orgJobsSearchPageUri}
                      data-cy={`button:view-organisation-jobs`}
                    >
                      Search opportunities
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="section-container-full bg-white border-none">
          <div className="suppress-section-container-padding">
            {nav && (
              <div className="border-b-2 border-gray-50 mt-4 md:mt-10 w-full">
                <OrganisationNav tabs={tabs} path={path} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
